import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-hot-toast";
import {
  FaWindows,
  FaApple,
  FaXbox,
  FaHeart,
  FaTimes,
  FaUser,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import {
  FiGrid,
  FiPlay,
  FiSearch,
  FiStar,
  FiZap,
  FiShoppingCart,
} from "react-icons/fi";
import { BiGitCompare } from "react-icons/bi";
import { DiAndroid } from "react-icons/di";
import { SiNintendoswitch, SiPlaystation, SiOculus, SiSteamdeck } from "react-icons/si";
import { TbDeviceVisionProFilled } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Modal } from "@mui/material";
import { BsGrid3X3Gap, BsListUl } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllActiveGamesWithPagination } from "../Redux/Slice/game.slice";
import {
  addToCart as addToCartAction,
  fetchCart,
} from "../Redux/Slice/cart.slice";
import {
  addToWishlist,
  removeFromWishlist,
  fetchWishlist,
} from "../Redux/Slice/wishlist.slice";
import BackgroundColor from "../component/BackgroundColor";
import ProductSkeleton from "../component/skeleton-loaders/ProductSkeleton";
import SEOHead from "../component/SEOHead";

const PLATFORMS_CONFIG = [
  {
    id: "windows",
    name: "Windows PC",
    icon: FaWindows,
    colorClass: "text-blue-500",
    glowClass: "shadow-blue-500/20 hover:border-blue-500/50",
    gradient: "from-blue-600 to-cyan-500",
  },
  {
    id: "playstation",
    name: "PlayStation",
    icon: SiPlaystation,
    colorClass: "text-indigo-500",
    glowClass: "shadow-indigo-500/20 hover:border-indigo-500/50",
    gradient: "from-indigo-600 to-blue-500",
  },
  {
    id: "xbox",
    name: "Xbox",
    icon: FaXbox,
    colorClass: "text-green-500",
    glowClass: "shadow-green-500/20 hover:border-green-500/50",
    gradient: "from-green-600 to-emerald-500",
  },
  {
    id: "nintendo",
    name: "Nintendo Switch",
    icon: SiNintendoswitch,
    colorClass: "text-red-500",
    glowClass: "shadow-red-500/20 hover:border-red-500/50",
    gradient: "from-red-600 to-orange-500",
  },
  {
    id: "quest",
    name: "Meta Quest VR",
    icon: SiOculus,
    colorClass: "text-sky-400",
    glowClass: "shadow-sky-400/20 hover:border-sky-400/50",
    gradient: "from-sky-500 to-indigo-500",
  },
  // {
  //   id: "visionpro",
  //   name: "Vision Pro",
  //   icon: TbDeviceVisionProFilled,
  //   colorClass: "text-purple-400",
  //   glowClass: "shadow-purple-400/20 hover:border-purple-400/50",
  //   gradient: "from-purple-600 to-pink-500",
  // },
  {
    id: "ios",
    name: "iOS App Store",
    icon: FaApple,
    colorClass: "text-gray-200",
    glowClass: "shadow-gray-200/20 hover:border-gray-200/50",
    gradient: "from-gray-600 to-gray-400",
  },
  {
    id: "android",
    name: "Android Play Store",
    icon: DiAndroid,
    colorClass: "text-emerald-400",
    glowClass: "shadow-emerald-400/20 hover:border-emerald-400/50",
    gradient: "from-emerald-500 to-lime-400",
  },
  {
    id: "steamdeck",
    name: "Steam Deck",
    icon: SiSteamdeck,
    colorClass: "text-blue-400",
    glowClass: "shadow-blue-400/20 hover:border-blue-400/50",
    gradient: "from-blue-600 to-indigo-500",
  },
];

export default function Platforms() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedPlatform, setSelectedPlatform] = useState("windows");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [grid, setGrid] = useState(3);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const [compareGames, setCompareGames] = useState([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [dots, setDots] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState({});

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPageButtons, setMaxPageButtons] = useState(5);
  const gamesPerPage = 12;

  // Handle resize to adjust number of pagination buttons
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setMaxPageButtons(3);
      } else if (window.innerWidth < 1024) {
        setMaxPageButtons(4);
      } else {
        setMaxPageButtons(6);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset page when platform, search or sort changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedPlatforms({});
  }, [selectedPlatform, searchTerm, sortBy]);

  // Redux state
  const {
    paginatedGames = [],
    totalGames = 0,
    totalPages = 1,
    loadingStates,
  } = useSelector((state) => state.game);

  const isLoadingGames = loadingStates.getAllActiveGamesWithPagination;
  const cartItems = useSelector((state) => state.cart?.items || []);
  const { wishlistStatus } = useSelector((state) => state.wishlist);
  const userId = localStorage.getItem("yoyouserId");

  useEffect(() => {
    window.scrollTo(0, 0);

    const token = localStorage.getItem("yoyoToken");
    if (userId && token) {
      dispatch(fetchCart());
      dispatch(fetchWishlist());
    }
  }, [dispatch, userId]);

  // Debounced API call for paginated platform games
  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(
        getAllActiveGamesWithPagination({
          page: currentPage,
          limit: gamesPerPage,
          search: searchTerm?.trim(),
          platform: selectedPlatform,
          sortBy: sortBy,
          isActive: true,
        })
      );
    }, 300); // 300ms debounce
    return () => {
      clearTimeout(handler);
    };
  }, [currentPage, selectedPlatform, searchTerm, sortBy, dispatch]);

  // Handle outside click for sort dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSortOpen &&
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSortOpen]);

  // Generate dropping animated dots
  useEffect(() => {
    const generateDots = () => {
      const newDots = [];
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight;
      for (let i = 0; i < 15; i++) {
        newDots.push({
          id: `drop-${i}`,
          x: Math.random() * containerWidth,
          startY: -20,
          delay: Math.random() * 5,
          duration: 3 + Math.random() * 3,
        });
      }
      setDots(newDots);
    };
    generateDots();
    window.addEventListener("resize", generateDots);
    return () => window.removeEventListener("resize", generateDots);
  }, []);

  const handleCardMouseEnter = (gameId) => {
    const videoEl = document.getElementById(`video-${gameId}`);
    if (videoEl) {
      videoEl.play().catch(() => { });
    }
  };

  const handleCardMouseLeave = (gameId) => {
    const videoEl = document.getElementById(`video-${gameId}`);
    if (videoEl) {
      videoEl.pause();
      videoEl.currentTime = 0;
    }
  };

  const isGameInCart = (gameId, platform) => {
    return cartItems.some(
      (item) =>
        String(item.game?._id || item.game) === String(gameId) &&
        item.platform === platform
    );
  };

  const toggleFavorite = (gameId) => {
    if (!userId) {
      navigate("/login");
      return;
    }
    const isInWishlist = wishlistStatus[gameId];
    if (isInWishlist) {
      dispatch(removeFromWishlist({ gameId }));
    } else {
      dispatch(addToWishlist({ gameId }));
    }
  };

  const handleToggleCompare = (game) => {
    if (compareGames.some((g) => g._id === game._id)) {
      setCompareGames((prev) => prev.filter((g) => g._id !== game._id));
    } else {
      if (compareGames.length >= 3) {
        toast.error("You can compare a maximum of 3 games.");
        return;
      }
      setCompareGames((prev) => [...prev, game]);
    }
  };

  const sortOptions = [
    { value: "popular", label: "Most Popular" },
    { value: "price", label: "Price: Low to High" },
    { value: "price_desc", label: "Price: High to Low" },
    { value: "newest", label: "Newest First" },
  ];

  return (
    <>
      <SEOHead
        title="Game Platforms"
        description="Browse games by platform on Yoyo Game. Find the best games for Windows PC, Android, iOS, PlayStation, Xbox, Nintendo Switch, Meta Quest VR, Steam Deck, and Apple Vision Pro."
        keywords="games by platform, PC games, Android games, iOS games, PlayStation games, Xbox games, Nintendo Switch games, Meta Quest VR games, Steam Deck games, Apple Vision Pro games"
        url="https://yoyokhel.com/platforms"
        breadcrumbs={[{ name: "Platforms", url: "https://yoyokhel.com/platforms" }]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Game Platforms – Yoyo Game",
          "description": "Browse games for all major gaming platforms including PC, consoles, mobile, and VR.",
          "url": "https://yoyokhel.com/platforms",
          "isPartOf": { "@type": "WebSite", "name": "Yoyo Game", "url": "https://yoyokhel.com" }
        }}
      />
      <BackgroundColor className="relative mx-auto w-[95%] sm:w-[92%] md:w-[90%] lg:max-w-[80%]">
        <div className="relative pt-24 pb-8 z-10">
          {/* Header Title */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <h1 className="text-3xl lg:text-5xl font-bold mb-3">
              Game <span className="gradient-text">Platforms</span>
            </h1>
            <p className="text-sm lg:text-base text-primary-light/60 max-w-2xl mx-auto">
              Browse and find games across your favorite systems. Select a platform to view compatible games, sizes, prices, and features.
            </p>
          </motion.div>

          {/* Platform Selection Tabs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 3xl:grid-cols-8 gap-4 mb-10">
            {PLATFORMS_CONFIG.map((platform) => {
              const Icon = platform.icon;
              const isSelected = selectedPlatform === platform.id;
              return (
                <motion.button
                  key={platform.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setSelectedPlatform(platform.id);
                    setSearchTerm("");
                  }}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border backdrop-blur-xl transition-all duration-300 ${isSelected
                    ? `bg-gradient-to-br ${platform.gradient} text-white border-transparent shadow-lg ${platform.glowClass}`
                    : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white"
                    }`}
                >
                  <Icon className={`w-5 h-5 sm:w-8 sm:h-8 mb-2 ${isSelected ? "text-white" : platform.colorClass}`} />
                  <span className="text-xs font-semibold text-center leading-tight">
                    {platform.name}
                  </span>
                </motion.button>
              );
            })}
          </div>

          {/* Filters and Controls */}
          <div className="bg-gradient-to-r from-white/[0.03] to-white/[0.01] backdrop-blur-2xl rounded-2xl p-4 mb-6 border border-white/10 relative z-20 shadow-[0_20px_50px_rgba(0,0,0,0.3)] hover:border-purple-500/20 transition-all duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Search Field */}
              <div className="flex-1 max-w-md relative flex items-center">
                <div className="relative w-full group">
                  <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/50 group-hover:text-purple-400 group-focus-within:text-purple-400 w-5 h-5 transition-colors duration-300" />
                  <input
                    type="text"
                    placeholder="Search platform games..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-10 py-2.5 bg-white/[0.04] border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/40 focus:border-purple-400/80 transition-all duration-300 backdrop-blur text-sm hover:bg-white/[0.06] hover:border-white/20"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                    >
                      <FaTimes className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Controls & Dropdowns */}
              <div className="flex flex-wrap items-center gap-3">
                {/* View Grid/List Control */}
                <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 gap-1">
                  <button
                    className={`p-2 rounded-lg font-semibold transition-all duration-300 text-lg flex items-center justify-center ${grid === 3
                      ? "bg-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                      }`}
                    onClick={() => setGrid(3)}
                    aria-label="Grid View"
                    title="Grid View"
                  >
                    <BsGrid3X3Gap className="w-4 h-4" />
                  </button>
                  <button
                    className={`p-2 rounded-lg font-semibold transition-all duration-300 text-lg flex items-center justify-center ${grid === 1
                      ? "bg-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                      }`}
                    onClick={() => setGrid(1)}
                    aria-label="List View"
                    title="List View"
                  >
                    <BsListUl className="w-4 h-4" />
                  </button>
                </div>

                {/* Sort Control */}
                <div className="relative inline-block text-left flex-1 sm:flex-initial" ref={sortDropdownRef}>
                  <button
                    onClick={() => setIsSortOpen(!isSortOpen)}
                    className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white hover:bg-white/[0.08] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500/40 backdrop-blur-md flex justify-between items-center w-full sm:w-48 text-sm"
                  >
                    <span className="truncate">
                      Sort: {sortOptions.find((o) => o.value === sortBy)?.label}
                    </span>
                    <svg
                      className={`w-4 h-4 ml-2 transition-transform duration-300 text-white/60 ${isSortOpen ? "rotate-180 text-purple-400" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isSortOpen && (
                    <div className="text-sm absolute right-0 mt-2 w-full sm:w-48 bg-slate-950/95 backdrop-blur-xl border border-white/10 rounded-xl text-white z-50 shadow-2xl shadow-purple-950/50 overflow-hidden">
                      {sortOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setIsSortOpen(false);
                          }}
                          className={`px-4 py-2.5 transition-all duration-200 cursor-pointer border-b border-white/5 last:border-0 ${sortBy === option.value
                            ? "bg-gradient-to-r from-purple-600/30 to-pink-600/30 text-purple-300 border-l-2 border-l-purple-500 font-semibold"
                            : "hover:bg-white/5 text-white/80"
                            }`}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Results Counter */}
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white tracking-wider">
              {PLATFORMS_CONFIG.find((p) => p.id === selectedPlatform)?.name} Games
            </h2>
            <p className="text-white/60 text-sm">
              Showing {totalGames} games available on this platform
            </p>
          </div>

          {/* Games Grid/List */}
          {isLoadingGames ? (
            <ProductSkeleton grid={grid} />
          ) : paginatedGames.length === 0 ? (
            <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10">
              <FiGrid className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <h3 className="text-white/60 text-2xl mb-2">No games found</h3>
              <p className="text-white/40">Try adjusting your search criteria or look at another platform.</p>
            </div>
          ) : (
            <div
              className={`grid ${grid === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4"
                } gap-6 xl:gap-8`}
            >
              {paginatedGames.map((game, index) => {
                const currentCardPlatform =
                  selectedPlatforms[game._id] ||
                  selectedPlatform ||
                  Object.keys(game.platforms || {}).filter(p => game.platforms[p]?.available)[0] ||
                  "windows";
                return (
                  <div
                    key={game._id}
                    className={`group relative bg-white/5 backdrop-blur-xl rounded-xl lg:rounded-3xl overflow-hidden border border-white/10 hover:border-purple-400/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20 ${grid === 1 ? "md:flex" : ""
                      }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    onMouseEnter={() => handleCardMouseEnter(game._id)}
                    onMouseLeave={() => handleCardMouseLeave(game._id)}
                    onClick={() => navigate(`/gamedetails/${game._id}`)}
                  >
                    {/* Image Container */}
                    <div
                      className={`relative overflow-hidden ${grid === 1 ? "w-full md:w-60 lg:w-64 md:h-full" : "h-48"
                        }`}
                    >
                      <img
                        src={game.cover_image?.url}
                        alt={game.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      {game.video?.url && (
                        <video
                          id={`video-${game._id}`}
                          src={game.video.url}
                          preload="none"
                          muted
                          loop
                          playsInline
                          className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>

                      {/* Action buttons */}
                      <div className="absolute top-4 right-4 flex gap-2 z-20">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleCompare(game);
                          }}
                          className={`p-2 rounded-full backdrop-blur-md transition-all duration-300 ${compareGames.some((cg) => cg._id === game._id)
                            ? "bg-purple-600 text-white scale-110"
                            : "bg-black/40 text-white hover:bg-purple-600 hover:scale-110"
                            }`}
                          title="Compare Game"
                        >
                          <BiGitCompare className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFavorite(game._id);
                          }}
                          className={`p-2 rounded-full backdrop-blur-md transition-all duration-300 ${wishlistStatus[game._id]
                            ? "bg-red-500 text-white scale-110"
                            : "bg-black/40 text-white hover:bg-red-500 hover:scale-110"
                            }`}
                        >
                          <FaHeart className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Play Hover Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <button className="bg-white/90 text-gray-900 p-4 rounded-full hover:bg-white hover:scale-110 transition-all duration-300 shadow-2xl">
                          <FiPlay className="w-6 h-6 ml-1" />
                        </button>
                      </div>
                    </div>

                    {/* Content details */}
                    <div
                      className={`p-3 xl:p-6 ${grid === 1 ? "md:flex-1" : ""
                        }`}
                    >
                      <h3 className="text-base lg:text-xl font-bold text-white mb-2 group-hover:text-purple-300 transition-colors line-clamp-1">
                        {game.title.replace(/"/g, "")}
                      </h3>
                      <p className="text-gray-400 text-xs lg:text-sm mb-4 line-clamp-3">
                        {game.description?.replace(/"/g, "")}
                      </p>

                      {/* Price and CTA */}
                      <div className="flex items-center md:items-start lg:items-center justify-between flex-row md:flex-col lg:flex-row mt-4 pt-4 border-t border-white/10">
                        <div className="text-white flex flex-col gap-1 text-sm lg:text-base">
                          <p className="">
                            Price - ${" "}
                            {game.platforms?.[selectedPlatform]?.price || 0}
                          </p>
                        </div>

                        {/* CTA button */}
                        {isGameInCart(game._id, selectedPlatform) ? (
                          <button
                            disabled
                            className="bg-gray-500 text-white px-1 py-2 lg:px-3 lg:py-2 text-sm rounded-md font-medium flex items-center gap-2 cursor-not-allowed mt-0 md:mt-2 lg:mt-0"
                          >
                            <FiShoppingCart className="w-4 h-4" />
                            Already Added
                          </button>
                        ) : !userId ? (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate("/login");
                            }}
                            className="bg-gradient-primary text-white p-2 lg:px-3 lg:py-2 text-sm rounded-md font-medium transition-all hover:scale-105 flex items-center gap-2 mt-0 md:mt-2 lg:mt-0"
                          >
                            <FaUser className="w-4 h-4" />
                            Login to Add
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              dispatch(
                                addToCartAction({
                                  gameId: game._id,
                                  platform: selectedPlatform,
                                  qty: 1,
                                })
                              );
                            }}
                            className="bg-gradient-primary text-white p-2 lg:px-3 lg:py-2 text-sm rounded-md font-medium transition-all hover:scale-105 flex items-center gap-2 mt-0 md:mt-2 lg:mt-0"
                          >
                            <FiShoppingCart className="w-4 h-4" />
                            Add To Cart
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center md:justify-end items-center mt-8 space-x-2">
              <button
                onClick={() => {
                  setCurrentPage((prev) => Math.max(1, prev - 1));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                disabled={currentPage === 1}
                className="p-2 rounded-md text-white hover:bg-gray-700/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                <FaChevronLeft />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                const startPage = Math.max(
                  1,
                  currentPage - Math.floor(maxPageButtons / 2)
                );
                const endPage = Math.min(
                  totalPages,
                  startPage + maxPageButtons - 1
                );

                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= startPage && page <= endPage)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => {
                        setCurrentPage(page);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className={`px-3 py-1.5 md:px-4 md:py-2 rounded-md text-sm sm:text-base font-medium transition-all duration-300 ring-[1px] ring-primary-light/10 ${currentPage === page
                        ? "bg-gradient-primary text-white"
                        : "text-white hover:bg-gray-700/40"
                        }`}
                    >
                      {page}
                    </button>
                  );
                }
                if (
                  (page === startPage - 1 && startPage > 2) ||
                  (page === endPage + 1 && endPage < totalPages - 1)
                ) {
                  return (
                    <span key={page} className="text-white">
                      ...
                    </span>
                  );
                }
                return null;
              })}

              <button
                onClick={() => {
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md text-white hover:bg-gray-700/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>

        {/* Sticky Compare Drawer */}
        {compareGames.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-7xl bg-[#0b0b0f]/95 backdrop-blur-xl p-4 z-40 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl transition-all duration-300">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-white text-sm lg:text-base font-bold flex items-center gap-2">
                <BiGitCompare className="text-purple-400 animate-pulse text-lg" />
                Compare ({compareGames.length}/3)
              </span>
              <div className="flex gap-2 flex-wrap">
                {compareGames.map((cg) => (
                  <div
                    key={cg._id}
                    className="bg-white/5 border border-white/10 px-3 py-1.5 rounded-xl flex items-center gap-2 hover:bg-white/10 transition-colors"
                  >
                    <img src={cg.cover_image?.url} alt="" className="w-8 h-8 rounded-lg object-cover border border-white/10" />
                    <span className="text-white text-xs font-semibold">{cg.title}</span>
                    <button
                      onClick={() => handleToggleCompare(cg)}
                      className="text-red-400 hover:text-red-300 p-0.5 rounded-full hover:bg-white/10 transition-colors"
                    >
                      <FaTimes size={10} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 w-full sm:w-auto justify-end">
              <button
                onClick={() => setCompareGames([])}
                className="text-white/60 hover:text-white text-xs lg:text-sm font-semibold transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={() => setIsCompareModalOpen(true)}
                className="bg-gradient-primary text-white px-6 py-2.5 rounded-xl text-xs lg:text-sm font-bold shadow-lg shadow-purple-500/20 hover:scale-105 hover:shadow-[0_0_15px_rgba(147,51,234,0.4)] transition-all"
              >
                Compare Now
              </button>
            </div>
          </div>
        )}

        {/* Comparison Modal */}
        <Modal
          open={isCompareModalOpen}
          onClose={() => setIsCompareModalOpen(false)}
          className="fixed inset-0 flex items-center justify-center z-[100] px-4 py-8 overflow-y-auto"
        >
          <div className="bg-[#0b0b0f]/95 backdrop-blur-2xl border border-purple-500/30 p-6 lg:p-10 text-white w-full max-w-5xl rounded-xl shadow-2xl outline-none max-h-[90vh] overflow-y-auto overflow-x-hidden scrollbar-hide relative">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />

            <div className="flex justify-between items-center mb-8 pb-4 border-b border-white/10">
              <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <BiGitCompare className="text-purple-400" />
                Game Comparison
              </h2>
              <button
                onClick={() => setIsCompareModalOpen(false)}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-colors"
              >
                <FaTimes size={16} />
              </button>
            </div>

            <div className="w-full overflow-x-auto no-scrollbar pb-2">
              <div className="min-w-[850px] md:min-w-0 grid grid-cols-1 md:grid-cols-4 gap-4 border border-white/10 rounded-2xl overflow-hidden divide-y md:divide-y-0 md:divide-x divide-white/10 bg-white/[0.02]">
                {/* Criteria Column */}
                <div className="p-4 space-y-8 font-bold text-purple-300/80 text-xs uppercase tracking-wider hidden md:block">
                  <div className="h-64 flex items-end pb-2 border-b border-white/5">Game</div>
                  <div className="h-12 flex items-center">Price</div>
                  <div className="h-12 flex items-center">Platform Support</div>
                  <div className="h-12 flex items-center">Rating</div>
                  {/* <div className="h-12 flex items-center">Download Size</div> */}
                  <div className="h-32 flex items-center">Windows Requirements</div>
                  <div className="h-32 flex items-center">Mobile & Console Specs</div>
                </div>

                {/* Game Columns */}
                {compareGames.map((cg) => {
                  const rating = cg.ratings?.length
                    ? (cg.ratings.reduce((acc, r) => acc + r.rating, 0) / cg.ratings.length).toFixed(1)
                    : "No ratings";

                  const winPlat = cg.platforms?.windows;
                  const iosPlat = cg.platforms?.ios;
                  const androidPlat = cg.platforms?.android;

                  // Find first available platform to add to cart
                  const firstAvailPlatform = Object.keys(cg.platforms || {}).find(k => cg.platforms[k]?.available);

                  return (
                    <div key={cg._id} className="p-4 space-y-8 text-sm text-white flex flex-col md:block">
                      {/* Game Details Header */}
                      <div className="md:h-64 flex flex-col items-center md:items-start justify-between gap-4 pb-4 border-b border-white/5">
                        <div className="flex flex-col items-center md:items-start gap-3 w-full">
                          <img src={cg.cover_image?.url} alt="" className="w-28 h-32 rounded-xl object-cover shadow-lg border border-white/10 group-hover:scale-105 transition-transform" />
                          <div className="text-center md:text-left min-w-0 w-full">
                            <h3 className="font-bold text-base line-clamp-1 text-white hover:text-purple-300 transition-colors" title={cg.title}>{cg.title}</h3>
                            <span className="text-[10px] uppercase font-semibold text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded-full mt-1 inline-block">
                              {cg.category?.categoryName || "Game"}
                            </span>
                          </div>
                        </div>

                        {/* Actions Row */}
                        <div className="w-full flex flex-col gap-2 mt-auto">
                          <button
                            onClick={() => {
                              setIsCompareModalOpen(false);
                              navigate(`/gamedetails/${cg._id}`);
                            }}
                            className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium py-1.5 px-3 rounded-lg text-xs transition"
                          >
                            View Details
                          </button>

                          {firstAvailPlatform ? (
                            isGameInCart(cg._id, firstAvailPlatform) ? (
                              <button
                                disabled
                                className="w-full bg-gray-500/20 text-gray-400 font-medium py-1.5 px-3 rounded-lg text-xs flex items-center justify-center gap-1.5 cursor-not-allowed border border-white/5"
                              >
                                <FiShoppingCart className="w-3.5 h-3.5" />
                                Already in Cart
                              </button>
                            ) : !userId ? (
                              <button
                                onClick={() => {
                                  setIsCompareModalOpen(false);
                                  navigate("/login");
                                }}
                                className="w-full bg-gradient-primary text-white font-medium py-1.5 px-3 rounded-lg text-xs flex items-center justify-center gap-1.5 transition hover:opacity-90"
                              >
                                <FaUser className="w-3.5 h-3.5" />
                                Login to Add
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  dispatch(
                                    addToCartAction({
                                      gameId: cg._id,
                                      platform: firstAvailPlatform,
                                      qty: 1,
                                    })
                                  );
                                }}
                                className="w-full bg-gradient-primary text-white font-medium py-1.5 px-3 rounded-lg text-xs flex items-center justify-center gap-1.5 transition hover:opacity-90"
                              >
                                <FiShoppingCart className="w-3.5 h-3.5" />
                                Add to Cart
                              </button>
                            )
                          ) : (
                            <button
                              disabled
                              className="w-full bg-white/5 text-gray-500 font-medium py-1.5 px-3 rounded-lg text-xs cursor-not-allowed border border-white/5"
                            >
                              Unavailable
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="h-12 flex items-center justify-between md:justify-start">
                        <span className="md:hidden font-bold text-purple-300/80 mr-2 text-xs uppercase tracking-wider">Price:</span>
                        <span className="font-bold text-green-400 text-base">
                          {(() => {
                            const priceVal = cg.platforms?.[selectedPlatform]?.price;
                            if (priceVal !== undefined) return `$${priceVal}`;
                            const prices = Object.values(cg.platforms || {})
                              .filter(p => p.available)
                              .map(p => p.price);
                            return prices.length > 0 ? `$${Math.min(...prices)}` : "Free";
                          })()}
                        </span>
                      </div>

                      {/* Platform Support */}
                      <div className="h-12 flex items-center justify-between md:justify-start gap-3">
                        <span className="md:hidden font-bold text-purple-300/80 mr-2 text-xs uppercase tracking-wider">Platforms:</span>
                        <div className="flex gap-2 text-white/60">
                          {winPlat?.available && <FaWindows className="text-blue-500 text-base" title="Windows" />}
                          {iosPlat?.available && <FaApple className="text-white text-base" title="iOS" />}
                          {androidPlat?.available && <DiAndroid className="text-green-500 text-base" title="Android" />}
                          {cg.platforms?.nintendo?.available && <SiNintendoswitch className="text-red-500 text-base" title="Nintendo Switch" />}
                          {cg.platforms?.quest?.available && <SiOculus className="text-sky-400 text-base" title="Meta Quest" />}
                          {cg.platforms?.playstation?.available && <SiPlaystation className="text-blue-500 text-base" title="PlayStation" />}
                          {cg.platforms?.xbox?.available && <FaXbox className="text-green-500 text-base" title="Xbox" />}
                          {cg.platforms?.visionpro?.available && <TbDeviceVisionProFilled className="text-purple-400 text-base" title="Vision Pro" />}
                          {cg.platforms?.steamdeck?.available && <SiSteamdeck className="text-blue-400 text-base" title="Steam Deck" />}
                        </div>
                      </div>

                      {/* Rating */}
                      <div className="h-12 flex items-center justify-between md:justify-start">
                        <span className="md:hidden font-bold text-purple-300/80 mr-2 text-xs uppercase tracking-wider">Rating:</span>
                        <span className="flex items-center gap-1 text-yellow-400 font-bold text-base">
                          <FiStar className="fill-current" />
                          {rating}
                        </span>
                      </div>

                      {/* <div className="h-12 flex items-center justify-between md:justify-start">
                      <span className="md:hidden font-bold text-purple-300/80 mr-2 text-xs uppercase tracking-wider">Size:</span>
                      <span className="font-medium">
                        {winPlat?.size || androidPlat?.size || iosPlat?.size || 
                         cg.platforms?.nintendo?.size || cg.platforms?.quest?.size || 
                         cg.platforms?.playstation?.size || cg.platforms?.xbox?.size || 
                         cg.platforms?.visionpro?.size || cg.platforms?.steamdeck?.size || "N/A"}
                      </span>
                    </div> */}

                      {/* Windows Specs */}
                      <div className="h-32 flex flex-col justify-center text-xs text-gray-300 space-y-1.5">
                        <span className="md:hidden font-bold text-purple-300/80 mb-1 text-xs uppercase tracking-wider">Windows Specs:</span>
                        {winPlat?.available ? (
                          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2.5 space-y-1">
                            <div className="line-clamp-1"><b>OS:</b> {winPlat.system_requirements?.os || "N/A"}</div>
                            <div className="line-clamp-1"><b>CPU:</b> {winPlat.system_requirements?.processor || "N/A"}</div>
                            <div className="line-clamp-1"><b>RAM:</b> {winPlat.system_requirements?.memory || "N/A"}</div>
                            <div className="line-clamp-1"><b>GPU:</b> {winPlat.system_requirements?.graphics || "N/A"}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500 italic">Not supported on Windows</span>
                        )}
                      </div>

                      {/* Mobile & Console Specs */}
                      <div className="h-32 flex flex-col justify-center text-xs text-gray-300 space-y-1.5 overflow-y-auto no-scrollbar">
                        <span className="md:hidden font-bold text-purple-300/80 mb-1 text-xs uppercase tracking-wider">Mobile & Console Specs:</span>
                        {androidPlat?.available || iosPlat?.available || cg.platforms?.nintendo?.available || cg.platforms?.quest?.available || cg.platforms?.playstation?.available || cg.platforms?.xbox?.available || cg.platforms?.visionpro?.available || cg.platforms?.steamdeck?.available ? (
                          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2.5 space-y-1 max-h-full overflow-y-auto no-scrollbar">
                            {androidPlat?.available && (
                              <div className="line-clamp-1"><b>Android:</b> {androidPlat.system_requirements?.android_version || "N/A"}</div>
                            )}
                            {iosPlat?.available && (
                              <div className="line-clamp-1"><b>iOS:</b> {iosPlat.system_requirements?.ios_version || "N/A"}</div>
                            )}
                            {cg.platforms?.nintendo?.available && (
                              <div className="line-clamp-1"><b>Switch:</b> {cg.platforms.nintendo.system_requirements?.device_compatibility || "N/A"}</div>
                            )}
                            {cg.platforms?.quest?.available && (
                              <div className="line-clamp-1"><b>Quest:</b> {cg.platforms.quest.system_requirements?.device_compatibility || "N/A"}</div>
                            )}
                            {cg.platforms?.playstation?.available && (
                              <div className="line-clamp-1"><b>PS:</b> {cg.platforms.playstation.system_requirements?.device_compatibility || "N/A"}</div>
                            )}
                            {cg.platforms?.xbox?.available && (
                              <div className="line-clamp-1"><b>Xbox:</b> {cg.platforms.xbox.system_requirements?.device_compatibility || "N/A"}</div>
                            )}
                            {cg.platforms?.visionpro?.available && (
                              <div className="line-clamp-1"><b>Vision Pro:</b> {cg.platforms.visionpro.system_requirements?.device_compatibility || "N/A"}</div>
                            )}
                            {cg.platforms?.steamdeck?.available && (
                              <div className="line-clamp-1"><b>Steam Deck:</b> {cg.platforms.steamdeck.system_requirements?.device_compatibility || "N/A"}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500 italic">No specs available</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Modal>
      </BackgroundColor>
    </>
  );
}